<template>
  <div>
    <!--Existing child/children on policy-->
    <div v-for="(child, idx) in children.existingChildren" :key="idx" class="existing-child">
      <SectionSeparatorWrapper headerText="Children" headerRequiredFieldsText="indicates required field" class="field" :separator="false">
        <h3 class="h6 sub-heading-underlined">{{ `Child ${parseInt(idx) + 1}` }}</h3>
        <!-- Name section -->
        <SectionSeparatorWrapper
          :separator="false"
          :helperText="child.keepOnPolicy || !under31YearsOld(child.DOB) ? informationReferenceData.childNameHelperText.informationText : ``"
          :fullHeight="true"
        >
          <MemberTag
            :firstName="child.firstName ? child.firstName : '&#8212;'"
            :lastName="child.lastName ? child.lastName : '&#8212;'"
            :showLastName="true"
            relationship="Child"
          />
        </SectionSeparatorWrapper>

        <!-- keep on policy? -->
        <div class="form-section">
          <FormField labelText="I would like to keep this child on the membership">
            <ul class="radio-group large">
              <li v-for="option in boolOptions" :key="option.returnValue">
                <input :id="`keep-on-policy-${idx}-${option.returnValue}`" :value="option.returnValue" type="radio" v-model="child.keepOnPolicy" />
                <label :for="`keep-on-policy-${idx}-${option.returnValue}`">{{ option.display }}</label>
              </li>
            </ul>
          </FormField>
        </div>

        <div class="no-longer-covered" v-if="!child.keepOnPolicy || !under31YearsOld(child.DOB)">
          <FormField>
            <Alert message="This child will no longer be covered on this policy." alertType="info" />
            <Alert
              v-if="!under31YearsOld(child.DOB)"
              message="The product selected covers dependants up to 31 years of age. Once they turn 31, they will need to take out their own cover."
              alertType="error"
            />
          </FormField>

          <!--Reason for termination-->
          <FormField labelText="Reason for termination" errorMessage="Please include a response for Reason for policy change.">
            <DropdownSelect
              :items="getMemberTerminationReasons"
              v-model="child.terminationReason"
              :initialSelect="getDropdownOption(getMemberTerminationReasons, child.terminationReason, 'Please select a reason')"
              :noDisabled="false"
              :id="`termination-reason-${idx}`"
            />
          </FormField>
          <!--New fund name-->
          <FormField labelText="New fund name" errorMessage="Please include a response for New fund name." v-if="isFundNameRequired(child.terminationReason)">
            <DropdownSelect
              :items="fundNamesTerminated"
              v-model="child.newFundName"
              :initialSelect="getDropdownOption(fundNamesTerminated, child.newFundName, 'Please select a fund name')"
              :noDisabled="false"
              :id="`fund-name-${idx}`"
            />
          </FormField>
        </div>
      </SectionSeparatorWrapper>
    </div>

    <template v-if="!currentProductAllowsChildren">
      <FormField>
        <Alert :message="CHILDREN_NOT_SUPPORTED" :isPlainText="false"></Alert>
      </FormField>
    </template>

    <template v-if="mustHaveChildren && children.existingChildren.length === children.terminations.length && children.newChildren.length === 0">
      <FormField>
        <Alert :message="ENFORCE_CHILDREN_ERROR_MESSAGE" :isPlainText="false"></Alert>
      </FormField>
    </template>

    <!-- No existing children - Adding kids question -->
    <template v-if="children.existingChildren.length === 0 && currentProductAllowsChildren">
      <SectionSeparatorWrapper headerText="Children" headerRequiredFieldsText="indicates required field" :separator="false" class="field">
        <h3 class="h6 sub-heading-underlined">Adding kids?</h3>
        <FormField labelText="If new kids are being added, capture the details for the new kids being added to the membership">
          <ul class="radio-group large">
            <li v-for="option in boolOptions" :key="`add-new-child-${option.display}`">
              <input type="radio" :id="`new-child-${option.display}`" :value="option.returnValue" v-model="children.addKidsQuestion" />
              <label :for="`new-child-${option.display}`">{{ option.display }}</label>
            </li>
          </ul>
        </FormField>
      </SectionSeparatorWrapper>
    </template>

    <!-- New children on policy -->
    <div v-for="(child, idx) in children.newChildren" :key="children.existingChildren.length + parseInt(idx)" class="new-child">
      <SectionSeparatorWrapper :separator="false">
        <h3 class="h6 sub-heading-underlined">{{ `Child ${children.existingChildren.length + 1 + parseInt(idx)}` }}</h3>
        <div v-if="apiStatus.duplicatePersonCheck.loading" class="loading-screen">
          <div class="form-grid">
            <Spinner></Spinner>
          </div>
        </div>
        <div class="form-section">
          <!-- First name -->
          <FormField
            :id="`new-child-first-name-${idx}`"
            labelText="First name"
            placeholder="e.g. John"
            type="text"
            v-model.lazy="child.firstName"
            :error="v$.children.newChildren.$each.$response?.$errors?.[idx]?.firstName?.length > 0"
            :errorMessage="getNameErrorMessage(idx, 'firstName')"
            :disabled="children.newChildren[idx].isDuplicate || children.newChildren[idx].duplicateCheckFailed || apiStatus.duplicatePersonCheck.loading"
            @input="searchDebounce(runDuplicatePersonSearch, idx)"
            @blur="runDuplicatePersonSearch(idx)"
            required
          />
        </div>
        <!-- Middle name -->
        <div class="form-section">
          <FormField
            :id="`new-child-middle-name-${idx}`"
            labelText="Middle name"
            placeholder="e.g. Fred"
            type="text"
            v-model.lazy="child.middleName"
            :disabled="children.newChildren[idx].isDuplicate || children.newChildren[idx].duplicateCheckFailed || apiStatus.duplicatePersonCheck.loading"
            :error="v$.children.newChildren.$each.$response?.$errors?.[idx]?.middleName?.length > 0"
            :errorMessage="getNameErrorMessage(idx, 'middleName')"
            :required="false"
          />
        </div>
        <!-- Last name -->
        <div class="form-section">
          <FormField
            :id="`new-child-last-name-${idx}`"
            labelText="Last name"
            placeholder="e.g. Citizen"
            type="text"
            v-model.lazy="child.lastName"
            :error="v$.children.newChildren.$each.$response?.$errors?.[idx]?.lastName?.length > 0"
            :errorMessage="getNameErrorMessage(idx, 'lastName')"
            :disabled="children.newChildren[idx].isDuplicate || children.newChildren[idx].duplicateCheckFailed || apiStatus.duplicatePersonCheck.loading"
            @input="searchDebounce(runDuplicatePersonSearch, idx)"
            @blur="runDuplicatePersonSearch(idx)"
            required
          />
        </div>
        <!-- Sex -->
        <FormField labelText="Select sex" :helperText="informationReferenceData.sex.helpText" required class="form-section">
          <ul class="radio-group large">
            <li v-for="sex in informationReferenceData.sex.options" :key="sex.returnValue">
              <input
                :id="`${sex.returnValue}${idx}`"
                :value="sex.returnValue"
                :disabled="children.newChildren[idx].isDuplicate || children.newChildren[idx].duplicateCheckFailed || apiStatus.duplicatePersonCheck.loading"
                @change="runDuplicatePersonSearch(idx)"
                type="radio"
                v-model="child.sex"
                :name="`sex + ${idx}`"
              />
              <label :for="`${sex.returnValue}${idx}`">{{ sex.display }}</label>
            </li>
          </ul>
        </FormField>
        <!-- DOB -->
        <FormField labelText="Date of birth" class="form-section half-width" required>
          <FormInput
            placeholder="DD / MM / YYYY"
            v-maska="'## / ## / ####'"
            type="text"
            v-model.lazy.trim="child.DOB"
            maxlength="14"
            :isDateInput="true"
            :id="`new-child-dob-${idx}`"
            :overrideClass="v$.children.newChildren.$each.$response?.$errors?.[idx]?.DOB?.length > 0 ? 'error' : null"
            :disabled="children.newChildren[idx].isDuplicate || children.newChildren[idx].duplicateCheckFailed || apiStatus.duplicatePersonCheck.loading"
            @input="searchDebounce(runDuplicatePersonSearch, idx)"
            @blur="runDuplicatePersonSearch(idx)"
          />
          <label v-if="v$.children.newChildren.$each.$response?.$errors?.[idx]?.DOB?.length > 0" :for="`newChild${idx}`" class="error">
            {{ getDOBErrorMessage(idx) }}
          </label>
          <Alert v-if="turning31In30Days(child.DOB, new Date())" class="inline-message">>
            <div>
              <p>{{ DOBErrorMessage.IS_ALMOST_31 }}</p>
            </div>
          </Alert>

          <Alert v-if="(v$.children.newChildren.$each.$response?.$errors?.[idx]?.DOB || []).some(e => e.$validator === 'mustBeUnder31YearsOld')" alertType="error" class="inline-message">
            <div>
              <p v-if="!isFamilyPlus">
                This policy can cover dependants and eligible students who are studying full-time, single and aged between 21 and 30. Whether young adults are working or still
                studying once they turn 31 it's time for them to take out their own cover.
              </p>
              <p v-else>
                This policy can cover dependants, eligible students who are studying full-time, single and aged between 21 and 30 and at least one dependant aged between 21 and 30
                years who is not studying full-time. Whether young adults are working or still studying once they turn 31 it's time for them to take out their own cover.
              </p>

              <p v-if="allChildren.length > 1">
                <template v-if="!isFamilyPlus">If one of your dependants is aged under 31 you</template>
                <template v-else>You</template>
                can continue with this application by removing the dependant aged 31 or above.
              </p>
              <p v-else>
                You cannot continue with this application. Please
                <a @click="redirectToChangeCoverStep('MyCover')" class="always-fresh">review and update your details</a>
                to explore cover options that do not include dependants and we'll help you find the health cover that better meets your need.
              </p>
            </div>
          </Alert>
        </FormField>

        <div class="form-grid">
          <ApiLoading v-if="children.newChildren[idx].duplicateCheckFailed" :apiError="children.newChildren[idx].currentDuplicateChild.fail">
            <p>
              Sorry, we couldn’t load the information.
              <button @click="runDuplicatePersonSearch(idx)" class="non-button-style">Reload</button>
              the result to continue
            </p>
          </ApiLoading>
        </div>
        <!-- Duplicate person alert -->
        <FormField v-if="children.newChildren[idx].isDuplicate">
          <Alert alertType="error" :message="DUPLICATE_PERSON_ERROR_MESSAGE"></Alert>
        </FormField>

        <!-- full time student and single -->
        <div v-if="withinStudentAge(child.DOB) && !isChildOnlyPolicy && !children.newChildren[idx].isDuplicate && !children.newChildren[idx].duplicateCheckFailed">
          <FormField
            labelText="Is this child a full-time student and single?"
            class="form-section"
            :error="(v$.children.newChildren.$each.$response?.$errors?.[idx]?.isStudent || []).some(e => e.$validator === 'required')"
            :errorMessage="`This information is required. Please make a selection.`"
          >
            <ul class="radio-group large">
              <li v-for="option in boolOptions" :key="option.returnValue">
                <input :id="`isStudent${option.returnValue}${idx}`" :value="option.returnValue" type="radio" v-model.lazy="child.isStudent" :name="`student + ${idx}`" />
                <label :for="`isStudent${option.returnValue}${idx}`">{{ option.display }}</label>
              </li>
            </ul>

            <Alert v-if="isFamilyPlus && child.isStudent === true && allChildren.length === 1" alertType="error" class="inline-message">
              <div>
                <p>
                  This policy can cover dependant and eligible students who are studying full-time, single and aged between 21 and 30 and at least one dependant aged between 21 and
                  30 years who is not studying full-time.
                </p>

                <p v-if="allChildren.length === 1">
                  Please review your dependant's details to ensure their student status is correct. Alternatively, please
                  <a @click="redirectToChangeCoverStep('MyCover')" class="always-fresh">review and update your dependant details</a>
                  and we'll help you find the health cover that better meets your need.
                </p>
              </div>
            </Alert>
          </FormField>
          <!-- Name of school, college, uni -->
          <div v-if="child.isStudent" class="form-section">
            <FormField
              labelText="Name of the school, college, university or TAFE at which you are attending"
              placeholder="e.g. DHL University"
              type="text"
              required
              v-model="child.university"
              :error="v$.children.newChildren.$each.$response?.$errors?.[idx]?.university?.length > 0"
              :errorMessage="getInstituteNameError(v$.children.newChildren.$each.$response?.$errors?.[idx]?.university || [])"
            />
            <!--year of study-->
            <div>
              <p>Year of study</p>
              <p>{{ getStudentDeclarationYear(child.studentStartDate) }}</p>
            </div>
            <!-- Declaration -->
            <FormField>
              <input type="checkbox" :id="`new-child-${idx}-declaration`" v-model="child.declaration" />
              <label :for="`new-child-${idx}-declaration`">
                {{ informationReferenceData.childDetailAcknowledgementText.informationText }}
                <span class="required-input">*</span>
              </label>
            </FormField>
          </div>
        </div>
      </SectionSeparatorWrapper>

      <!-- Transferring from another fund -->
      <SectionSeparatorWrapper v-if="!children.newChildren[idx].isDuplicate && !children.newChildren[idx].duplicateCheckFailed" :separator="false">
        <div class="form-section">
          <h3 class="h6 sub-heading-underlined">Transferring from another fund</h3>
          <FormField labelText="This person is transferring from another fund">
            <ul class="radio-group large">
              <li v-for="option in boolOptions" :key="`from-other-funds-${option.display}`">
                <input
                  type="radio"
                  :id="`from-other-funds-${idx}-${option.display}`"
                  v-model.lazy="child.transferFromOthers"
                  :name="`from-other-funds-${idx}-${option.display}`"
                  :value="option.returnValue"
                  :error="(v$.children.newChildren.$each.$response?.$errors?.[idx]?.transferFromOthers || []).some(e => e.$validator === 'required')"
                  @change="!child.transferFromOthers"
                  :errorMessage="`This information is required. Please make a selection.`"
                />
                <label :for="`from-other-funds-${idx}-${option.display}`">{{ option.display }}</label>
              </li>
            </ul>
          </FormField>
        </div>
        <template v-if="child.transferFromOthers === true">
          <!-- Previous fund -->
          <div class="form-section" v-for="(fund, index) in child.previousFunds" :key="index">
            <FormField
              labelText="Which fund are you transferring from?"
              class="form-section"
              :error="(v$.children.newChildren.$each.$response?.$errors?.[idx]?.previousFunds?.[0]?.$response?.$errors?.[index]?.fundCode || []).some(e => e.$validator === 'required')"
              :errorMessage="`This information is required. Please make a selection.`"
            >
              <DropdownSelect
                :items="fundNamesNewPerson"
                v-model="fund.fundCode"
                :noDisabled="false"
                :initialSelect="getDropdownOption(fundNamesNewPerson, fund.fundCode, 'Please select a fund name')"
              />
            </FormField>
            <FormField
              labelText="What was your member number at your previous health fund?"
              placeholder="XXXXXXXXX"
              type="text"
              class="form-section"
              :id="fund.memberNumber"
              v-model.lazy="fund.memberNumber"
              required
              :error="v$.children.newChildren.$each.$response?.$errors?.[idx]?.previousFunds?.[0]?.$response?.$errors?.[index]?.memberNumber?.length > 0"
              :errorMessage="getFundErrorMessage(idx, index)"
              :disabled="false"
            />
            <FormField>
              <p>By providing the details of your previous fund you authorise Defence Health to request cancellation of the products held with your previous health fund.</p>
            </FormField>
            <div v-if="child.previousFunds.length > 1" class="form-section">
              <a class="always-fresh" @click.prevent="removeFund(child.previousFunds, index)" role="button" href="#">Remove fund</a>
            </div>
          </div>
          <div class="form-section">
            <a class="always-fresh" v-if="child.previousFunds.length < 2" @click.prevent="addAnotherFund(child.previousFunds, idx)" role="button" href="#">
              Add another fund to switch from
            </a>
          </div>
        </template>
      </SectionSeparatorWrapper>

      <!-- Remove child link -->
      <div v-if="allChildren.length > 1" class="form-section">
        <a class="always-fresh" id="removeChild" @click.prevent="removeChild(idx)" role="button" href="#">Remove child</a>
      </div>
    </div>

    <!-- Add another child link-->
    <div class="form-section">
      <a
        class="always-fresh"
        id="addAnotherChildButton"
        v-if="children.existingChildren.length + children.newChildren.length < 15 && children.existingChildren.length + children.newChildren.length > 0"
        @click.prevent="addAnotherChild()"
        role="button"
        href="#"
      >
        Add another child
      </a>
    </div>
  </div>
</template>

<!-- eslint-disable import/no-extraneous-dependencies -->
<script>
import isEqual from 'lodash.isequal';
import {
  isPastDate,
  under31YearsOld,
  stringWithInvalidSpace,
  stringWithSymbols,
  withinStudentAge,
  isFundNameRequired,
  getStudentDeclarationStartAndEndDate,
  convertISOToDisplay,
  getStudentDeclarationYear,
  formatFormDate,
  isEligibleChild,
  reverseFormatFormDate,
  turning31In30Days,
  isPOBoxAddress,
} from '@general/js/utils';
import { DropdownSelect, Spinner } from '@general/js/components';
import { Alert, ApiLoading, FormField, FormInput, MemberTag, SectionSeparatorWrapper } from '@/components';
import { useVuelidate } from '@vuelidate/core';
import { helpers, maxLength, required, requiredIf } from '@vuelidate/validators';
import { JOIN_NOW_NAME_VALIDATOR, DATE_VALIDATOR } from '@general/js/validators';
import { formatPhoneNumber, getProductName, sortByObjectProperty, isEmptyString, findPolicyHolder } from '@/utils';
import { CoverComposition, DOBErrorMessage, NameErrorMessage } from '@general/js/enums';
import { OperationType } from '@/enums';
import cloneDeep from 'lodash.clonedeep';
import AddRemovePersonMixins from './AddRemovePersonMixins';

let triggerDuplicateSearch;
const apiTriggerTime = 1000;

export default {
  setup() { return { v$: useVuelidate() }; },

  name: 'Children',
  mixins: [AddRemovePersonMixins],

  components: {
    Alert,
    ApiLoading,
    DropdownSelect,
    FormField,
    FormInput,
    MemberTag,
    SectionSeparatorWrapper,
    Spinner,
  },

  props: {
    informationReferenceData: {
      type: Object,
      required: true,
    },
    // Fund names using `dhl_hiccode` when adding a new partner
    fundNamesNewPerson: {
      type: Array,
      required: true,
    },
    // Fund names using `dhl_code` when terminating a partner
    fundNamesTerminated: {
      type: Array,
      required: true,
    },
    terminationReasons: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      boolOptions: [
        {
          display: 'Yes',
          returnValue: true,
        },
        {
          display: 'No',
          returnValue: false,
        },
      ],
      children: {
        existingChildren: [],
        newChildren: [],
        addKidsQuestion: null,
      },
      childrenPayloads: [],
      childrenTerminations: [],
      isActive: false,
      currentProductAllowsChildren: null,
      mustHaveChildren: null,
      policyHolder: {},
      DOBErrorMessage,
    };
  },

  created() {
    this.getPolicyHolder();
    this.fetchCurrentChildren();
  },

  beforeMount() {
    if (this.childrenData && this.childrenData.newChildren && this.childrenData.newChildren.length) {
      // Pre-fill data for new children when coming back to this step
      this.children.newChildren = this.childrenData.newChildren;
    }
    // Pre-fill add kids question from vuex store
    this.children.addKidsQuestion = this.childrenData.addKidsQuestion;
  },

  mounted() {
    this.coverCompositionChildrenPermissions();
  },

  activated() {
    this.isActive = true;
    this.v$.$touch();
    this.createChildrenPayload();
    this.$nextTick(() => {
      this.$emit('formUpdated', {
        response: { ...this.children, payloads: this.childrenPayloads, terminations: this.childrenTerminations },
        isInvalid:
          this.v$.$invalid || this.v$.$error || this.atLeastOneChildIsDuplicate || this.atLeastOneChildDuplicateSearchApiFailed,
      });
    });
  },

  deactivated() {
    this.isActive = false;
  },

  validations() {
    return this.validationObj;
  },

  computed: {
    validationObj() {
      const obj = {
        children: {
          hasIneligibleDependent: this.hasIneligibleDependentOnPolicy,
          existingChildren: {
            $each: helpers.forEach({
              keepOnPolicy: {
                required,
                isSelected: (value, child) => {
                  // keepOnPolicy needs to be false if child is over 31 years old
                  if (!under31YearsOld(child.DOB)) return !child.keepOnPolicy;
                  // Otherwise pass this validation
                  return true;
                },
              },
              terminationReason: {
                required: requiredIf((_value, child) => {
                  // required only if keepOnPolicy = false
                  return !child.keepOnPolicy || !under31YearsOld(child.DOB);
                }),
              },
              newFundName: {
                required: requiredIf((_value, child) => {
                  // required only for to another fund termination reasons
                  return this.isFundNameRequired(child.terminationReason);
                }),
              },
            }),
          },
          newChildren: {
            $each: helpers.forEach({
              firstName: {
                required,
                JOIN_NOW_NAME_VALIDATOR,
                stringWithInvalidSpace,
                maxLength: maxLength(160),
              },
              middleName: {
                JOIN_NOW_NAME_VALIDATOR,
                stringWithInvalidSpace,
                maxLength: maxLength(160),
              },
              lastName: {
                required,
                JOIN_NOW_NAME_VALIDATOR,
                stringWithInvalidSpace,
                maxLength: maxLength(160),
              },
              sex: {
                required,
              },
              DOB: {
                required,
                DATE_VALIDATOR,
                isPastDate,
                mustBeUnder31YearsOld(value) {
                  return under31YearsOld(value);
                },
              },
              isDuplicate: (currentChild) => {
                if (currentChild.isDuplicate) {
                  // fail validation by returning false if child is duplicate so that the member cannot proceed with the Change Cover flow
                  return false;
                }
                return true;
              },
              isStudent: {
                required: requiredIf((_value, child) => {
                  return withinStudentAge(child.DOB) && !this.isChildOnlyPolicy; // Student question hidden if child/children only policy
                }),
              },
              university: {
                required: requiredIf((_value, child) => {
                  return child.isStudent;
                }),
                JOIN_NOW_NAME_VALIDATOR,
                stringWithInvalidSpace,
                maxLength: maxLength(160),
              },
              declaration: {
                required: requiredIf((_value, child) => {
                  return child.isStudent;
                }),
                isSelected: (value, child) => {
                  // declaration needs to be true if child is kept on policy and a student
                  if (child.isStudent) return value;
                  // Otherwise pass this validation
                  return true;
                },
              },
              transferFromOthers: {
                required,
              },
              previousFunds: {
                $each: helpers.forEach({
                  fundCode: {
                    required: requiredIf((_value, fund) => {
                      const childObject = this.children.newChildren[fund.childIndex];
                      return childObject?.transferFromOthers === true;
                    }),
                  },
                  memberNumber: {
                    required: requiredIf((_value, fund) => {
                      const childObject = this.children.newChildren[fund.childIndex];
                      return childObject?.transferFromOthers === true;
                    }),
                    stringWithSymbols,
                    maxLength: maxLength(19),
                  },
                }),
              },
            }),
          },
        },
      };
      return obj;
    },

    allChildren() {
      if (this.children.existingChildren && this.children.newChildren) {
        return this.children.existingChildren.concat(this.children.newChildren);
      }
      return [];
    },

    getMemberTerminationReasons() {
      const memberTerminationReasonOptions = [...this.terminationReasons.memberReasons, ...this.terminationReasons.bothReasons];
      return sortByObjectProperty(memberTerminationReasonOptions, 'description').reverse();
    },

    compositionRequiresStudentDeclaration() {
      const currentComposition = this.currentComposition;
      // Show if family/family+ or single family/single family+
      const requiredFor = [CoverComposition.FAMILY, CoverComposition.FAMILY_PLUS, CoverComposition.SINGLE_PARENT, CoverComposition.SINGLE_PARENT_PLUS];

      if (requiredFor.includes(currentComposition)) {
        return true;
      }

      return false;
    },

    isChildOnlyPolicy() {
      return this.currentComposition === CoverComposition.CHILDREN_ONLY;
    },

    isFamilyCover() {
      return [CoverComposition.FAMILY, CoverComposition.SINGLE_PARENT].includes(this.currentComposition);
    },

    isFamilyPlus() {
      return [CoverComposition.FAMILY_PLUS, CoverComposition.SINGLE_PARENT_PLUS].includes(this.currentComposition);
    },

    atLeastOneNonStudentAndAnother31Plus() {
      if (this.children.existingChildren) {
        const allChildren = this.children.existingChildren.concat(this.children.newChildren);
        let firstNonStudentIndex; // non student means aged 20yrs 11months - 31yrs (incl) AND NOT studying full-time
        for (let i = 0; i < allChildren.length; i++) {
          if (allChildren[i].keepOnPolicy !== false && allChildren[i].isStudent === false && under31YearsOld(reverseFormatFormDate(allChildren[i].DOB))) {
            firstNonStudentIndex = i;
            break;
          }
        }

        const nonFirstNonStudentWith31OrMore = allChildren.filter((child, i) => {
          if (child.keepOnPolicy !== false) {
            return i !== firstNonStudentIndex && !under31YearsOld(reverseFormatFormDate(child.DOB));
          }
          return false;
        });

        return firstNonStudentIndex !== undefined && nonFirstNonStudentWith31OrMore.length > 0;
      }

      return false;
    },

    atLeastOneEligibleStudentWithAnotherIneligibleDependant() {
      const allChildren = this.children.existingChildren.concat(this.children.newChildren);

      return (
        allChildren.some((child) => {
          const startDate = new Date();
          if (child.keepOnPolicy === false) {
            return false;
          }
          // check if there are any eligible child or student
          return (child.isStudent === true && withinStudentAge(child.DOB, startDate)) || isEligibleChild(child.DOB, startDate);
        }) &&
        allChildren.some((child) => {
          const startDate = new Date();
          const childrenFieldInvalid = this.v$.children.newChildren.$invalid;
          if (child.keepOnPolicy === false) {
            return false;
          }
          // check if there are any ineligible dependents
          return (
            (child.isStudent === false && withinStudentAge(child.DOB, startDate) && !childrenFieldInvalid) ||
            (!isEligibleChild(child.DOB, startDate) && !withinStudentAge(child.DOB, startDate) && !childrenFieldInvalid)
          );
        })
      );
    },
    // IF there are no children/eligible students at all, fail validation
    noStudentsNorChildrenAtAll() {
      const allChildren = this.children.existingChildren.concat(this.children.newChildren);
      const startDate = new Date();
      const childrenFieldInvalid = this.v$.children.newChildren.$invalid;
      if (this.childrenPayloads && this.childrenPayloads.length > 1) {
        return allChildren.every((child) => {
          if (child.keepOnPolicy === false) {
            return true;
          }
          // check if there are no children/student
          return !childrenFieldInvalid && !((child.isStudent === true && withinStudentAge(child.DOB, startDate)) || isEligibleChild(child.DOB, startDate));
        });
      }
      return false;
    },

    atLeastOneChildIsDuplicate() {
      return this.children.newChildren.some((child) => {
        return child.isDuplicate === true;
      });
    },

    atLeastOneChildDuplicateSearchApiFailed() {
      return this.children.newChildren.some((child) => {
        return child.duplicateCheckFailed === true;
      });
    },
  },

  methods: {
    isFundNameRequired,
    under31YearsOld,
    withinStudentAge,
    turning31In30Days,
    getStudentDeclarationYear,
    formatPhoneNumber,
    getProductName,

    searchDebounce(fn, params) {
      clearTimeout(triggerDuplicateSearch);
      triggerDuplicateSearch = setTimeout(async () => {
        await fn(params);
      }, apiTriggerTime);
    },

    getPolicyHolder() {
      if (this.allMembersData) {
        const policyHolder = findPolicyHolder(this.allMembersData, false, true);
        if (policyHolder) {
          const residentialAddress = policyHolder.residentialAddress;

          if (residentialAddress) {
            this.policyHolder.residentialAddress = {
              ...this.getAddress(policyHolder, false, false),
            };
          } else {
            this.policyHolder.residentialAddress = {};
          }

          this.policyHolder.differentPostalAddress = !!policyHolder.address.addressLine1;
          this.policyHolder.POBox = isPOBoxAddress(policyHolder.address.addressLine1);
          if (this.policyHolder.differentPostalAddress) {
            this.policyHolder.postalAddress = this.getAddress(policyHolder, this.policyHolder.differentPostalAddress, this.policyHolder.isPOBox);
          } else {
            this.policyHolder.postalAddress = { ...this.policyHolder.residentialAddress };
          }
        }
      }
    },

    async runDuplicatePersonSearch(index) {
      // only run if not already loading AND new child exists
      if (!this.apiStatus.duplicatePersonCheck.loading && this.children.newChildren[index]) {
        const childErrors = this.v$.children.newChildren.$each.$response?.$errors?.[index] || {};
        const firstNameInvalid = childErrors?.firstName?.length > 0;
        const lastNameInvalid = childErrors?.lastName?.length > 0;
        const genderInvalid = childErrors?.sex?.length > 0;
        const dobInvalid = childErrors?.DOB?.length > 0;

        // only run if all four fields are valid
        if (!firstNameInvalid && !lastNameInvalid && !genderInvalid && !dobInvalid) {
          const currentFirstName = this.children.newChildren[index].firstName;
          const currentLastName = this.children.newChildren[index].lastName;
          const currentGender = this.children.newChildren[index].sex;
          const currentDOB = formatFormDate(this.children.newChildren[index].DOB);

          let isPersonDuplicate = false;
          try {
            isPersonDuplicate = await this.duplicatePersonSearch(currentFirstName, currentLastName, currentGender, formatFormDate(currentDOB));
            this.children.newChildren[index].isDuplicate = isPersonDuplicate;
          } catch (e) {
            logger.log(e);
            this.children.newChildren[index].duplicateCheckFailed = true;
            this.children.newChildren[index].isDuplicate = false;
          }

          // If duplicate, need to raise a duplicate person work item in CRM
          if (isPersonDuplicate) {
            const payload = {
              firstName: currentFirstName,
              lastName: currentLastName,
              dob: currentDOB,
              sex: currentGender,
              needsAnalysisInfo: {},
            };
            const request = {
              workItemCategory: 'Duplicate Person Check Member Portal',
              title: 'Duplicate Contact in Change Cover Request',
              payload: { payload },
            };
            // make api call (will need to await if error action is required)
            this.raiseWorkItem(request);
          }
        }
      }
    },

    resetNewChildDetails(index) {
      this.children.newChildren[index].firstName = '';
      this.children.newChildren[index].middleName = '';
      this.children.newChildren[index].lastName = '';
      this.children.newChildren[index].sex = '';
      this.children.newChildren[index].DOB = '';
      this.children.newChildren[index].currentDuplicateChild = { fail: false };
    },

    fetchCurrentChildren() {
      const childrenArray = this.getDependantList();
      const children = this.allMembersData.filter((el) => {
        const elRelationshipValue = el.relationship.name.toLowerCase();
        return childrenArray.includes(elRelationshipValue) && el?.latestMemberStatus.toLowerCase() === 'active';
      });

      children.forEach((child, index) => {
        const initialPostalAddress = {
          addressLine1: child.address.addressLine1,
          addressLine2: child.address.addressLine2,
          addressLine3: child.address.addressLine3,
          suburb: child.address.suburb,
          state: child.address.state,
          postcode: child.address.postcode,
          dpId: child.address.dpId,
        };

        let childObject = {
          keepOnPolicy: true, // default for existing children
          firstName: child.firstName,
          middleName: child.middleName,
          lastName: child.lastName,
          initialPostalAddress,
          postalAddress: this.policyHolder.postalAddress,
          terminationReason: null,
          newFundName: null,
          personCode: child.personCode, // Used to determine if details have changed when creating payload below
          DOB: convertISOToDisplay(child.dob),
        };

        // Pre-fill data when coming back from confirmation step
        if (this.childrenData && this.childrenData.existingChildren && this.childrenData.existingChildren.length > 0 && this.childrenData.existingChildren[index]) {
          childObject = { ...childObject, ...this.childrenData.existingChildren[index] };
        }

        this.children.existingChildren.push(childObject);
      });
    },

    hasIneligibleDependentOnPolicy(children) {
      const terminatedCount = children.existingChildren.filter(c => !c.keepOnPolicy || !under31YearsOld(c.DOB)).length;
      const allExistingChildrenTerminated = terminatedCount === children.existingChildren.length;
      const newChildrenBeingAdded = children.newChildren.length > 0;
      const noNewChildrenBeingAdded = children.newChildren.length === 0;

      // if current product does NOT allow children, but either not all existing children are being terminated OR new children are being added, then fail validation
      if (!this.currentProductAllowsChildren && (!allExistingChildrenTerminated || newChildrenBeingAdded)) {
        return false; // return false (fail validation)
      }

      // if current product must have children, but all existing children are being terminated AND no new children are being added, then fail validation
      if (this.mustHaveChildren && allExistingChildrenTerminated && noNewChildrenBeingAdded) {
        return false;
      }

      // return true (pass validation)
      return true;
    },

    addAnotherChild() {
      const newChild = {
        firstName: '',
        middleName: '',
        lastName: '',
        sex: '',
        DOB: '',
        isStudent: null,
        university: '',
        postalAddress: this.policyHolder.postalAddress,
        transferFromOthers: null,
        previousFunds: [{ fundCode: '', memberNumber: '', childIndex: this.children.newChildren.length }],
        studentStartDate: getStudentDeclarationStartAndEndDate().studentStartDate,
        studentEndDate: getStudentDeclarationStartAndEndDate().studentEndDate,
        declaration: false,
        currentDuplicateChild: { fail: false },
        isDuplicate: false,
        duplicateCheckFailed: false,
      };
      // Create a copy of the current newChildren array
      const newChildrenCopy = [...this.children.newChildren];

      // Push the newChild into the copied array
      newChildrenCopy.push(newChild);

      // Replace the old array with the updated copy
      this.children.newChildren = newChildrenCopy;
    },

    addAnotherFund(funds, newChildIndex) {
      const newFund = { fundCode: '', memberNumber: '', childIndex: newChildIndex };
      funds.push(newFund);
    },

    removeFund(funds, idx) {
      funds.splice(idx, 1);
    },

    removeChild(idx) {
      this.children.newChildren.splice(idx, 1);
      this.modalIndex = 0; // reset modal index if child gets removed since modal no longer exists

      // Update childIndex for all new children based on new index post removal of child
      this.children.newChildren.forEach((child, index) => {
        child.previousFunds.forEach((fund) => {
          fund.childIndex = index;
        });
      });
      this.v$.children.$touch();
    },

    getNameErrorMessage(index, keyName) {
      const errors = this.v$.children.newChildren.$each.$response?.$errors?.[index]?.[keyName] || [];
      if (keyName !== 'middleName') {
        if (errors.some(e => e.$validator === 'maxLength')) {
          return NameErrorMessage.IS_OVER_LENGTH;
        }
        if (errors.some(e => e.$validator === 'JOIN_NOW_NAME_VALIDATOR' || e.$validator === 'stringWithInvalidSpace')) {
          return NameErrorMessage.IS_INVALID;
        }
        if (errors.some(e => e.$validator === 'required')) {
          if (keyName === 'firstName') return `${NameErrorMessage.IS_EMPTY} First name.`;
          return `${NameErrorMessage.IS_EMPTY} Last name.`;
        }

        return '';
      }
      if (errors.some(e => e.$validator === 'maxLength')) {
        return NameErrorMessage.IS_OVER_LENGTH;
      }
      if (errors.some(e => e.$validator === 'JOIN_NOW_NAME_VALIDATOR' || e.$validator === 'stringWithInvalidSpace')) {
        return NameErrorMessage.IS_INVALID;
      }
      return '';
    },

    getDOBErrorMessage(index) {
      const errors = this.v$.children.newChildren.$each.$response?.$errors?.[index]?.DOB || [];
      if (errors.some(e => e.$validator === 'required')) {
        return `${DOBErrorMessage.IS_EMPTY} Date of birth.`;
      }
      if (errors.some(e => e.$validator === 'isPastDate') && !errors.some(e => e.$validator === 'DATE_VALIDATOR')) {
        return DOBErrorMessage.IS_FUTURE_DATE;
      }
      if (errors.some(e => e.$validator === 'mustBeUnder31YearsOld') && !errors.some(e => e.$validator === 'DATE_VALIDATOR')) {
        return DOBErrorMessage.IS_OVER_31;
      }
      return DOBErrorMessage.IS_INVALID;
    },

    getInstituteNameError(errors) {
      if (errors.some(e => e.$validator === 'maxLength')) {
        return NameErrorMessage.IS_OVER_LENGTH;
      }
      if (errors.some(e => e.$validator === 'JOIN_NOW_NAME_VALIDATOR' || e.$validator === 'stringWithInvalidSpace')) {
        return NameErrorMessage.IS_INVALID;
      }
      if (errors.some(e => e.$validator === 'required')) {
        return `${NameErrorMessage.IS_EMPTY} Institute name.`;
      }
      return '';
    },

    getFundErrorMessage(childIdx, fundIdx) {
      const childErrors = this.v$.children.newChildren.$each.$response?.$errors?.[childIdx];
      const fundErrors = childErrors?.previousFunds?.[0]?.$response?.$errors?.[fundIdx]?.memberNumber || [];
      if (fundErrors.some(e => e.$validator === 'required')) return 'Please include a response for your member number.';
      if (fundErrors.some(e => e.$validator === 'maxLength' || e.$validator === 'stringWithSymbols')) return 'Please enter a combination of up to 19 letters or numbers.';
      return '';
    },

    isStudentDeclarationChanged(oldDeclaration, newDeclaration) {
      return !(
        oldDeclaration?.isStudent === newDeclaration.isStudent &&
        oldDeclaration?.instituteName === newDeclaration.instituteName &&
        oldDeclaration?.startDate === newDeclaration.startDate &&
        oldDeclaration?.endDate === newDeclaration.endDate
      );
    },

    createChildrenPayload() {
      this.childrenPayloads = [];
      this.childrenTerminations = [];
      // add to this.children.payload
      this.children.existingChildren.forEach((child) => {
        const payload = {};
        payload.personCode = child.personCode;
        payload.dateOfBirth = child.DOB;

        if (child.keepOnPolicy === false) {
          payload.terminationReason = child.terminationReason;
          if (child.newFundName) {
            payload.fundName = child.newFundName;
          }
          this.childrenTerminations.push(payload);
        } else {
          this.childrenPayloads.push(this.createExistingChildPayload(child, payload));
        }
      });

      // Add new child payload
      this.children.newChildren.forEach((child) => {
        const payload = {};
        payload.op = OperationType.ADD;
        payload.firstName = child.firstName;
        if (!isEmptyString(child.middleName)) payload.middleName = child.middleName;
        payload.lastName = child.lastName;
        payload.gender = child.sex;
        payload.dateOfBirth = formatFormDate(child.DOB);
        payload.transferFromOthers = child.transferFromOthers;
        if (child.transferFromOthers) {
          // need to use clonedeep otherwise it returns a reference to object and modifying values modifies the original object
          payload.previousFunds = cloneDeep(
            child.previousFunds.map((f) => {
              return {
                fundCode: f.fundCode.split('-')[0], // Split to only get the inbound transfer code and not include fund name,
                memberNumber: f.memberNumber,
                isAutomaticTransfer: this.isFundAutomaticTransfer(f.fundCode),
              };
            }),
          );
        } else {
          payload.previousFunds = [];
        }
        payload.isStudent = !!child.isStudent;
        payload.postalAddress = {
          op: OperationType.ADD,
          ...child.postalAddress,
        };

        if (withinStudentAge(child.DOB) && child.isStudent && !this.isChildOnlyPolicy) {
          payload.studentDeclaration = {
            op: OperationType.ADD,
            startDate: child.studentStartDate,
            endDate: child.studentEndDate,
            instituteName: child.university,
          };
        } else {
          payload.studentDeclaration = null;
        }

        this.childrenPayloads.push(payload);
      });
    },

    createExistingChildPayload(child, payload) {
      // Postal address payload
      const initialPostal = child.initialPostalAddress;
      const currentPostal = child.postalAddress;

      payload.dateOfBirth = formatFormDate(child.DOB);

      let postalOp = OperationType.NONE;
      if (initialPostal?.addressLine1 && !isEqual(initialPostal, currentPostal)) {
        postalOp = OperationType.REPLACE;
      } else if (!initialPostal?.addressLine1) {
        postalOp = OperationType.ADD;
      }

      payload.postalAddress = {
        op: postalOp,
        ...currentPostal,
      };

      payload.op = postalOp === OperationType.NONE ? OperationType.NONE : OperationType.REPLACE;
      return payload;
    },

    coverCompositionChildrenPermissions() {
      this.currentProductAllowsChildren = [
        CoverComposition.SINGLE_PARENT,
        CoverComposition.SINGLE_PARENT_PLUS,
        CoverComposition.FAMILY,
        CoverComposition.FAMILY_PLUS,
        CoverComposition.CHILDREN_ONLY,
      ].some((comp) => {
        return this.compositionsAllowedForCurrentProducts.includes(comp);
      });

      this.mustHaveChildren = ![CoverComposition.SINGLE, CoverComposition.COUPLE].some((comp) => {
        return this.compositionsAllowedForCurrentProducts.includes(comp);
      });

      if (!this.currentProductAllowsChildren) {
        this.children.addKidsQuestion = false;
      }
    },
  },

  watch: {
    children: {
      handler() {
        this.createChildrenPayload();
        if (this.isActive) {
          this.$nextTick(() => {
            this.$emit('formUpdated', {
              response: { ...this.children, payloads: this.childrenPayloads, terminations: this.childrenTerminations },
              isInvalid:
                this.v$.$invalid || this.v$.$error || this.atLeastOneChildIsDuplicate || this.atLeastOneChildDuplicateSearchApiFailed,
            });
          });
        }
      },
      deep: true,
    },
    'apiStatus.duplicatePersonCheck.loading': {
      handler() {
        this.$emit('apiLoading', { apiLoading: this.apiStatus.duplicatePersonCheck.loading });
      },
    },
    'children.addKidsQuestion': {
      handler(ans) {
        if (ans) {
          this.addAnotherChild();
          // needs to reset validation, otherwise will show error for newly added child
          this.v$.$reset();
        } else {
          // remove all new children
          this.children.newChildren = [];
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.name-row {
  display: flex;
  .name-col {
    flex-basis: 100%;
    flex: 1;
  }
}

.form-field {
  @include grid-layout(null, spacer(1));
  @include grid-template(2fr 1fr);
  row-gap: 0;
  margin-bottom: rem(10);

  .slotted-items {
    @include grid-position(1, 1, null, 2);

    &.with-label {
      @include grid-position(1, 2);
    }
  }
}

:deep(.form-section) {
  .names {
    margin-bottom: spacer(2);
  }
}

.bottom-margin {
  margin-bottom: 10px;
}

.loading-screen {
  position: absolute;
  background: $WHITE;
  backdrop-filter: none;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 999999;
  opacity: 1;
}

.form-grid {
  display: grid;
  top: 50%;
  grid-template-columns: 2fr 1fr;
  align-items: center;

  @include desktop {
    column-gap: $desktop_spacing;
  }

  @include tablet {
    column-gap: $tablet_spacing;
  }

  @include mobile {
    column-gap: $mobile_spacing;
    grid-template-columns: 100%;
    grid-template-rows: auto;
  }
}

:deep(#spinner) {
  text-align: center;
}

:deep(.spinner) {
  div {
    &::after {
      top: 48px;
      left: 38px;
      width: 4px;
      height: 22px;
    }
  }
}

:deep(.modal-table) {
  width: 100%;
  margin-bottom: 0;
  text-align: right;

  tr {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  th,
  td {
    border-bottom: none;
    padding-left: 0;
  }

  .break-long-email {
    word-break: break-all;
  }

  a {
    margin-top: 10px;
  }
}
</style>
